import { useMutation, useQuery } from '@tanstack/react-query'
import api from '../axios-client'
import { queryClient } from '../query-client'

export type RuleComparisonOperator =
    | '>'
    | '>='
    | '<'
    | '<='
    | '=='
    | '!='
    | 'contains'
    | 'not_contains'
    | 'exists'
    | 'not_exists'

export type RuleCondition =
    | {
          type: 'comparison'
          attribute: string
          operator: RuleComparisonOperator
          value?: string | number | boolean | null
      }
    | {
          type: 'group'
          operator: 'and' | 'or'
          conditions: RuleCondition[]
      }
    | {
          type: 'window_count'
          attribute: string
          where: {
              operator: RuleComparisonOperator
              value?: string | number | boolean | null
          }
          windowSeconds: number
          countOperator: RuleComparisonOperator
          count: number
      }

export type RuleNotificationConfig = {
    channels: Array<'in_app' | 'email' | 'sms' | 'webhook'>
    recipients: Array<string | { type: 'user'; userId: string } | { type: 'email'; email: string }>
    notifyOnResolved?: boolean
}

export type RuleAssignment = {
    type: 'model' | 'devices'
    deviceIds?: string[]
}

export type Rule = {
    id: string
    modelId: string
    name: string
    description: string | null
    severity: 'info' | 'warning' | 'critical'
    enabled: boolean
    triggerCondition: RuleCondition
    resolveCondition: RuleCondition | null
    triggerForSeconds: number
    resolveForSeconds: number
    cooldownSeconds: number
    persistentAlert: boolean
    notificationConfig: RuleNotificationConfig
    attributeKeys: string[]
    assignment: RuleAssignment & {
        devices?: Array<{ id: string; name: string }>
    }
}

export type RulePayload = {
    modelId: string
    name: string
    description?: string | null
    severity: Rule['severity']
    enabled: boolean
    triggerCondition: RuleCondition
    resolveCondition?: RuleCondition | null
    triggerForSeconds: number
    resolveForSeconds: number
    cooldownSeconds: number
    persistentAlert?: boolean
    notificationConfig: RuleNotificationConfig
    assignment: RuleAssignment
}

export const useGetRules = (modelId?: string) =>
    useQuery<Rule[]>({
        queryKey: ['rules', modelId],
        queryFn: async () => {
            const response = await api.get('/api/v1/rules', {
                params: modelId ? { modelId } : undefined,
            })
            return response.data.data
        },
        retry: false,
        enabled: !!modelId,
    })

export const useCreateRule = () =>
    useMutation({
        mutationFn: async (data: RulePayload) => {
            const response = await api.post('/api/v1/rules', data)
            return response.data.data
        },
        onSuccess: (_data, variables) => {
            queryClient.refetchQueries({ queryKey: ['rules', variables.modelId] })
        },
    })

export const useUpdateRule = () =>
    useMutation({
        mutationFn: async ({ id, data }: { id: string; data: RulePayload }) => {
            const response = await api.put(`/api/v1/rules/${id}`, data)
            return response.data.data
        },
        onSuccess: (_data, variables) => {
            queryClient.refetchQueries({ queryKey: ['rules', variables.data.modelId] })
            queryClient.refetchQueries({ queryKey: ['rule-events'] })
        },
    })

export const useDeleteRule = () =>
    useMutation({
        mutationFn: async ({ id }: { id: string; modelId: string }) => {
            const response = await api.delete(`/api/v1/rules/${id}`)
            return response.data.data
        },
        onSuccess: (_data, variables) => {
            queryClient.refetchQueries({ queryKey: ['rules', variables.modelId] })
            queryClient.refetchQueries({ queryKey: ['rule-events'] })
        },
    })

export const useGetRuleEvents = (modelId?: string) =>
    useQuery({
        queryKey: ['rule-events', modelId],
        queryFn: async () => {
            const response = await api.get('/api/v1/rules/events', {
                params: modelId ? { modelId, limit: 50 } : undefined,
            })
            return response.data.data
        },
        retry: false,
        enabled: !!modelId,
    })

export const useGetNotificationFeed = () =>
    useQuery({
        queryKey: ['rule-notifications'],
        queryFn: async () => {
            const response = await api.get('/api/v1/rules/notifications')
            return response.data.data
        },
        retry: false,
        refetchInterval: 30_000,
    })
